// src/components/Inventario/MateriaPrima/composables/useMateriaPrimaForm.js
import { computed, reactive, ref } from "vue";
import { useFormDraft } from "@/composables/useFormDraft";

export function useMateriaPrimaForm() {
  const getToday = () => {
    const d = new Date();
    const year  = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day   = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getTimeNow = () => {
    const d = new Date();
    const hours   = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  // Un id único por intento de envío: viaja con el payload como
  // "idempotency_key" para que el backend pueda reconocer un reintento
  // (botón doble-tap, recarga de página tras un error de red, etc.) y
  // devolver el registro ya creado en vez de duplicarlo.
  function generarIdempotencyKey() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `mp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  const initialState = {
    fecha: getToday(),
    hora: getTimeNow(),

    operador: "",
    operadorLabel: "",

    maquina: "",
    maquinaLabel: "",

    producto: "",
    productoLabel: "",
    productoCode: "",

    color: "",
    colorLabel: "",
    colorHex: "",

    cantidadPigmentoGr: "",
    aditivo: false,

    tpuItems: [
      {
        id: 1,
        proveedor: "",
        proveedorLabel: "",
        cantidad: "",
        unidad: "kg",
        unidadLabel: "kg",
      },
    ],

    molidaCantidad: "",
    molidaUnidad: "kg",
    molidaUnidadLabel: "kg",

    comentarios: "",

    folio: "",
    submittedAt: "",
  };

  const form = reactive({ ...initialState, idempotencyKey: generarIdempotencyKey() });

  const currentStep = ref(1);
  const { completeDraft, startNewDraft } = useFormDraft({
    key: "divisan:draft:materia-prima",
    form,
    currentStep,
    version: 1,
    maxStep: 5,
  });

  const canGoBack = computed(() => {
    return currentStep.value > 1 && currentStep.value < 6;
  });

  const isLastInputStep = computed(() => {
    return currentStep.value === 5;
  });

  function parseNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function formatKg(value) {
    return parseNumber(value).toFixed(2);
  }

  function formatGr(value) {
    return parseNumber(value).toFixed(2);
  }

  function createTpuItem(id) {
    return {
      id,
      proveedor: "",
      proveedorLabel: "",
      cantidad: "",
      unidad: "kg",
      unidadLabel: "kg",
    };
  }

  function addTpuItem() {
    if (form.tpuItems.length >= 4) return;

    const nextId = form.tpuItems.length
      ? Math.max(...form.tpuItems.map((item) => item.id)) + 1
      : 1;

    form.tpuItems.push(createTpuItem(nextId));
  }

  function removeTpuItem(id) {
    if (form.tpuItems.length <= 1) return;

    form.tpuItems = form.tpuItems.filter((item) => item.id !== id);
  }

  function validateStep(step = currentStep.value) {
    if (step === 1) {
      return Boolean(form.fecha && form.hora && form.operador && form.maquina);
    }

    if (step === 2) {
      return Boolean(
        form.producto &&
          form.color &&
          form.cantidadPigmentoGr !== "" &&
          parseNumber(form.cantidadPigmentoGr) >= 0,
      );
    }

    if (step === 3) {
      return form.tpuItems.every((item) => {
        return Boolean(
          item.proveedor &&
            item.cantidad !== "" &&
            parseNumber(item.cantidad) > 0 &&
            item.unidad,
        );
      });
    }

    if (step === 4) {
      return (
        form.molidaCantidad !== "" &&
        parseNumber(form.molidaCantidad) >= 0 &&
        Boolean(form.molidaUnidad)
      );
    }

    if (step === 5) {
      return true;
    }

    return true;
  }

  function buildPayload() {
  return {
    tipo_reporte: "materia_prima",
    fecha_registro: new Date().toISOString(),

    fecha: form.fecha,
    hora: form.hora,

    id_operador: form.operador,
    id_maquina: form.maquina,
    id_producto: form.producto,
    id_color: form.color,

    cantidad_pigmento: parseNumber(form.cantidadPigmentoGr),
    aditivo: Boolean(form.aditivo),

    tpu_items: form.tpuItems.map((item, index) => ({
      numero: index + 1,
      tpu_id: item.proveedor,       // item.proveedor guarda el id del TPU seleccionado
      cantidad: parseNumber(item.cantidad),
      unidad: item.unidad,
    })),

    molida: {
      cantidad: form.molidaCantidad === "" ? null : parseNumber(form.molidaCantidad),
      unidad: form.molidaCantidad === "" ? null : form.molidaUnidad,
    },

    comentarios: form.comentarios || "",
    idempotency_key: form.idempotencyKey,
  };
}

  function resetForm() {
    Object.assign(form, {
      ...initialState,
      fecha: getToday(),
      hora: "",
      aditivo: false,
      tpuItems: [
        {
          id: 1,
          proveedor: "",
          proveedorLabel: "",
          cantidad: "",
          unidad: "kg",
          unidadLabel: "kg",
        },
      ],
      molidaCantidad: "",
      molidaUnidad: "kg",
      molidaUnidadLabel: "kg",
      comentarios: "",
      folio: "",
      submittedAt: "",
      idempotencyKey: generarIdempotencyKey(),
    });

    currentStep.value = 1;
    startNewDraft();
  }

  return {
    form,
    currentStep,
    canGoBack,
    isLastInputStep,
    parseNumber,
    formatKg,
    formatGr,
    validateStep,
    buildPayload,
    resetForm,
    completeDraft,
    addTpuItem,
    removeTpuItem,
  };
}
